/**
 * G586 / G592 — Admin password reset slideout.
 *
 * Per V2 design contract (§5.5 + §3.13), user-facing actions with form
 * input or multi-phase state live in a Slideout, not a centered Modal.
 * The file name keeps "Modal" for backward-compatibility with import
 * sites; the export name is also kept as PasswordResetModal so consumers
 * don't have to change. A `PasswordResetSlideout` alias is also exported
 * for callers that want the literal V2 name.
 *
 * Two phases inside the Slideout body:
 *   1. Confirm + delivery-mode picker (LINK vs TEMP_PASSWORD).
 *   2. Success — render the credential exactly once. The temp password
 *      is never re-fetchable; closing the slideout forgets it. The reset
 *      link is also one-shot but the admin can re-issue if lost.
 *
 * Props:
 *   isOpen     — boolean
 *   onClose    — () => void
 *   user       — target user object ({id, email, name, role})
 *   onSuccess  — optional (result) => void  // called after a successful reset
 */
import React, { useEffect, useState } from 'react';
import { Copy, Check, AlertTriangle, ExternalLink, Mail, KeyRound, ShieldAlert } from 'lucide-react';
import { Button } from './Button';
import { Pill } from './Pill';
import { Slideout } from './Slideout';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const MODE_LINK = 'LINK';
const MODE_TEMP = 'TEMP_PASSWORD';

export function PasswordResetModal({ isOpen, onClose, user, onSuccess }) {
  const { addToast } = useToast();
  const [mode, setMode] = useState(MODE_LINK);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  /* `result` is the post-success state. It carries either resetUrl+expiresAt
   * (LINK mode) or tempPassword (TEMP_PASSWORD mode). Once set, we render
   * phase 2; closing the slideout forgets it permanently. */
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null); // which field was just copied (for the tick state)

  // Reset internal state every open so a re-open never leaks a prior session's
  // credential into the new phase-1 view. Also resets when the target user
  // changes mid-open (defensive — e.g. row swap from a parent re-render).
  useEffect(() => {
    if (isOpen) {
      setMode(MODE_LINK);
      setSubmitting(false);
      setError(null);
      setResult(null);
      setCopied(null);
    }
  }, [isOpen, user?.id]);

  if (!user) return null;

  const targetName = user.name || user.fullName || user.email || 'this user';

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.adminResetUserPassword(user.id, mode);
      if (!res || res.ok === false) {
        setError(res?.error || 'Reset failed');
        setSubmitting(false);
        return;
      }
      const data = res.data || res;
      setResult(data);
      if (typeof onSuccess === 'function') onSuccess(data);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      addToast({ tone: 'success', message: 'Copied to clipboard' });
      // Auto-clear the "copied" tick after a moment.
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1500);
    } catch (e) {
      addToast({ tone: 'danger', message: 'Copy failed — select + copy manually' });
    }
  };

  // ── Slideout header (always present; mode-aware copy) ────────────────
  const header = (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <KeyRound size={14} className="text-v2-action-strong" aria-hidden="true" />
        <Pill tone={result ? 'success' : 'muted'} size="sm" dot={!!result}>
          {result ? 'Issued' : 'Reset'}
        </Pill>
      </div>
      <h2 className="text-[16px] font-bold tracking-tight m-0">
        {result
          ? (result.mode === MODE_LINK ? 'Reset link ready' : 'Temporary password ready')
          : 'Reset password'}
      </h2>
      <p className="text-[12px] text-v2-ink-secondary mt-1 mb-0">
        {result
          ? <>For <span className="font-medium text-v2-ink">{targetName}</span>
              {result.targetEmail ? <> &middot; <span className="text-v2-ink-muted">{result.targetEmail}</span></> : null}
            </>
          : <>You're resetting the password for{' '}
              <span className="font-semibold text-v2-ink">{targetName}</span>
              {user.email && targetName !== user.email
                ? <> <span className="text-v2-ink-muted">({user.email})</span></>
                : null}.
            </>
        }
      </p>
    </div>
  );

  // ── Slideout footer (mode-aware CTAs) ───────────────────────────────
  const footer = result ? (
    <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
  ) : (
    <>
      <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
      <Button
        variant="primary"
        size="sm"
        onClick={handleSubmit}
        disabled={submitting}
      >{submitting ? 'Resetting…' : (mode === MODE_LINK ? 'Issue reset link' : 'Generate temp password')}</Button>
    </>
  );

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={result ? 'Password reset issued' : `Reset password for ${targetName}`}
      header={header}
      footer={footer}
      width={520}
      /* While submitting, don't let a stray backdrop click drop the
       * in-flight request — same pattern as the Add/Edit User slideout. */
      dismissOnBackdrop={!submitting}
    >
      {result ? (
        // ── Phase 2: success ───────────────────────────────────────
        <div className="space-y-4">
          {result.mode === MODE_LINK ? (
            <>
              <div>
                <label className="block text-[12px] font-medium text-v2-ink mb-1">Reset link</label>
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={result.resetUrl || ''}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 h-9 px-2.5 rounded-v2-sm border border-v2-border-default bg-v2-muted text-[12px] font-mono-v2 text-v2-ink"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={copied === 'url' ? <Check size={13} /> : <Copy size={13} />}
                    onClick={() => handleCopy(result.resetUrl, 'url')}
                  >Copy</Button>
                  {result.resetUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconLeft={<ExternalLink size={13} />}
                      onClick={() => window.open(result.resetUrl, '_blank', 'noopener,noreferrer')}
                      title="Open in a new tab"
                    >Open</Button>
                  )}
                </div>
                {result.expiresAt && (
                  <div className="text-[11.5px] text-v2-ink-muted mt-1.5">
                    Expires {new Date(result.expiresAt).toLocaleString()}. One-time use.
                  </div>
                )}
              </div>
              <div className="p-3 rounded-v2-sm bg-v2-action-soft border border-v2-action/30 text-[12px] text-v2-ink flex items-start gap-2">
                <Mail size={14} className="text-v2-action flex-shrink-0 mt-0.5" />
                <span>Share this link with {targetName} via your usual secure channel. When they click it they'll be prompted to set a new password.</span>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-[12px] font-medium text-v2-ink mb-1">Temporary password</label>
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={result.tempPassword || ''}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 h-9 px-2.5 rounded-v2-sm border border-v2-border-default bg-v2-muted text-[13px] font-mono-v2 tracking-wider text-v2-ink"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={copied === 'pw' ? <Check size={13} /> : <Copy size={13} />}
                    onClick={() => handleCopy(result.tempPassword, 'pw')}
                  >Copy</Button>
                </div>
              </div>
              <div className="p-3 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12px] text-v2-ink flex items-start gap-2">
                <ShieldAlert size={14} className="text-v2-danger flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-v2-danger mb-0.5">Shown only once</div>
                  This password is not stored anywhere after you close this dialog. Copy it now and share it with {targetName} via a secure channel. They should change it on first login.
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        // ── Phase 1: confirm + mode picker ─────────────────────────
        <div className="space-y-2">
          <label className={
            'flex items-start gap-3 p-3 rounded-v2-sm border cursor-pointer transition-colors '
            + (mode === MODE_LINK
                ? 'border-v2-action bg-v2-action-soft'
                : 'border-v2-border-default hover:bg-v2-muted')
          }>
            <input
              type="radio"
              className="mt-0.5"
              checked={mode === MODE_LINK}
              onChange={() => setMode(MODE_LINK)}
              name="reset-mode"
            />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-v2-ink">Send a reset link</div>
              <div className="text-[11.5px] text-v2-ink-muted mt-0.5">
                Backend mints a one-time token (24h). You'll get the URL to share — the user sets their own password. <span className="font-medium text-v2-ink">Recommended.</span>
              </div>
            </div>
          </label>

          <label className={
            'flex items-start gap-3 p-3 rounded-v2-sm border cursor-pointer transition-colors '
            + (mode === MODE_TEMP
                ? 'border-v2-action bg-v2-action-soft'
                : 'border-v2-border-default hover:bg-v2-muted')
          }>
            <input
              type="radio"
              className="mt-0.5"
              checked={mode === MODE_TEMP}
              onChange={() => setMode(MODE_TEMP)}
              name="reset-mode"
            />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-v2-ink">Generate a temporary password</div>
              <div className="text-[11.5px] text-v2-ink-muted mt-0.5">
                Strong random password shown to you exactly once. Use when email isn't available or in air-gapped environments.
              </div>
            </div>
          </label>

          {error && (
            <div className="mt-3 p-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[11.5px] text-v2-danger flex items-start gap-1.5">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}
    </Slideout>
  );
}

/* G592 — alias under the literal V2 name for new call sites. The legacy
 * PasswordResetModal export above is kept so the Team page row action
 * doesn't need to be touched. */
export const PasswordResetSlideout = PasswordResetModal;

export default PasswordResetModal;
